import React, { useState, useEffect, useCallback } from 'react';
import { Store, Plus, ImagePlus, Trash2, Star, FileText } from 'lucide-react';
import { maquinasService, UPLOADS_BASE_URL } from '../../../services/api';
import Modal from '../PuntoV/Modal';
import DocumentosPanel from './DocumentosPanel';

const TIPOS = [
  { id: 'snacks', label: 'Snacks' },
  { id: 'bebidas', label: 'Bebidas' },
  { id: 'cafe', label: 'Café' },
  { id: 'combo', label: 'Combo' }
];
const MONEDAS = ['USD', 'EUR', 'VES', 'USDT'];

const VACIA = {
  codigo: '', nombre: '', tipo: 'snacks', descripcion: '', precio_venta: '', moneda: 'USD', stock: 0,
  capacidad_unidades: '', formas_pago_maquina: '', consumo_electrico: '', largo_cm: '', ancho_cm: '',
  alto_cm: '', peso_kg: '', garantia_meses: '', tiempo_entrega: '', requisitos_instalacion: '',
  argumentos_venta: '', activo: true
};

// Intl no reconoce USDT como moneda: se muestra con el codigo al lado.
const fmtPrecio = (valor, moneda) => {
  const n = Number(valor);
  if (['USD', 'EUR', 'VES'].includes(moneda)) {
    return n.toLocaleString('es-VE', { style: 'currency', currency: moneda, maximumFractionDigits: 2 });
  }
  return `${n.toLocaleString('es-VE', { maximumFractionDigits: 2 })} ${moneda}`;
};

const urlFoto = (ruta) => `${UPLOADS_BASE_URL}${ruta}`;
const etiquetaTipo = (tipo) => TIPOS.find((t) => t.id === tipo)?.label || tipo;

const NUMERICOS = ['precio_venta', 'stock', 'capacidad_unidades', 'largo_cm', 'ancho_cm', 'alto_cm', 'peso_kg', 'garantia_meses'];

// PostgreSQL devuelve los numeric como texto con ceros ("1800.0000").
const aFormulario = (m) => {
  const f = {
    ...VACIA,
    ...Object.fromEntries(Object.keys(VACIA).map((k) => [k, m[k] ?? VACIA[k]])),
    formas_pago_maquina: (m.formas_pago_maquina || []).join(', ')
  };
  NUMERICOS.forEach((c) => { if (f[c] !== '') f[c] = String(Number(f[c])); });
  return f;
};

const aDatos = (f) => ({
  ...f,
  formas_pago_maquina: f.formas_pago_maquina.split(',').map((s) => s.trim()).filter(Boolean)
});

const Campo = ({ etiqueta, ayuda, children, ancho = '' }) => (
  <label className={`block ${ancho}`}>
    <span className="block text-xs font-medium text-gray-600 mb-1">{etiqueta}</span>
    {children}
    {ayuda && <span className="block text-[11px] text-gray-400 mt-0.5">{ayuda}</span>}
  </label>
);

const ENTRADA = 'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const FormularioMaquina = ({ inicial, onGuardada, onClose }) => {
  const [id, setId] = useState(inicial?.id || null);
  const [form, setForm] = useState(inicial ? aFormulario(inicial) : VACIA);
  const [fotos, setFotos] = useState(inicial?.fotos || []);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');

  const cambiar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const recargarFotos = async (maquinaId) => {
    const res = await maquinasService.obtener(maquinaId);
    setFotos(res.data.fotos || []);
    onGuardada();
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError('');
    try {
      if (id) {
        await maquinasService.actualizar(id, aDatos(form));
        onGuardada();
        onClose();
      } else {
        const res = await maquinasService.crear(aDatos(form));
        setId(res.data.id);
        onGuardada();
      }
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const accionFoto = async (accion) => {
    setSubiendo(true);
    setError('');
    try {
      await accion();
      await recargarFotos(id);
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción');
    } finally {
      setSubiendo(false);
    }
  };

  const alElegirArchivos = (e) => {
    const archivos = Array.from(e.target.files || []);
    e.target.value = '';
    if (archivos.length) accionFoto(() => maquinasService.subirFotos(id, archivos));
  };

  return (
    <Modal
      titulo={form.nombre || 'Nueva máquina'}
      subtitulo={id ? `Código ${form.codigo}` : 'Primero guarda la ficha; después podrás subir las fotos'}
      icono={Store}
      ancho="xl"
      bloqueado={guardando || subiendo}
      onClose={onClose}
      pie={(
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100">Cerrar</button>
          <button type="submit" form="form-maquina" disabled={guardando} className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {guardando ? 'Guardando...' : id ? 'Guardar cambios' : 'Crear máquina'}
          </button>
        </>
      )}
    >
      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <form id="form-maquina" onSubmit={guardar} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Campo etiqueta="Código *"><input required className={ENTRADA} value={form.codigo} onChange={cambiar('codigo')} /></Campo>
        <Campo etiqueta="Nombre *" ancho="md:col-span-2"><input required className={ENTRADA} value={form.nombre} onChange={cambiar('nombre')} /></Campo>

        <Campo etiqueta="Tipo *">
          <select className={ENTRADA} value={form.tipo} onChange={cambiar('tipo')}>
            {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Precio de venta *" ayuda="Precio de lista, sin impuestos">
          <input required type="number" min="0" step="0.01" className={ENTRADA} value={form.precio_venta} onChange={cambiar('precio_venta')} />
        </Campo>
        <Campo etiqueta="Moneda">
          <select className={ENTRADA} value={form.moneda} onChange={cambiar('moneda')}>
            {MONEDAS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Campo>

        <Campo etiqueta="Stock (unidades)"><input type="number" min="0" step="1" className={ENTRADA} value={form.stock} onChange={cambiar('stock')} /></Campo>
        <Campo etiqueta="Capacidad (productos)"><input type="number" min="1" step="1" className={ENTRADA} value={form.capacidad_unidades} onChange={cambiar('capacidad_unidades')} /></Campo>
        <Campo etiqueta="Garantía (meses)"><input type="number" min="0" step="1" className={ENTRADA} value={form.garantia_meses} onChange={cambiar('garantia_meses')} /></Campo>

        <Campo etiqueta="Formas de pago que acepta la máquina" ayuda="Separadas por coma: efectivo, tarjeta, QR" ancho="md:col-span-2">
          <input className={ENTRADA} value={form.formas_pago_maquina} onChange={cambiar('formas_pago_maquina')} />
        </Campo>
        <Campo etiqueta="Consumo eléctrico"><input className={ENTRADA} placeholder="Ej. 220 V, 400 W" value={form.consumo_electrico} onChange={cambiar('consumo_electrico')} /></Campo>

        <Campo etiqueta="Largo (cm)"><input type="number" min="0" step="0.1" className={ENTRADA} value={form.largo_cm} onChange={cambiar('largo_cm')} /></Campo>
        <Campo etiqueta="Ancho (cm)"><input type="number" min="0" step="0.1" className={ENTRADA} value={form.ancho_cm} onChange={cambiar('ancho_cm')} /></Campo>
        <Campo etiqueta="Alto (cm)"><input type="number" min="0" step="0.1" className={ENTRADA} value={form.alto_cm} onChange={cambiar('alto_cm')} /></Campo>

        <Campo etiqueta="Peso (kg)"><input type="number" min="0" step="0.1" className={ENTRADA} value={form.peso_kg} onChange={cambiar('peso_kg')} /></Campo>
        <Campo etiqueta="Tiempo de entrega" ancho="md:col-span-2"><input className={ENTRADA} placeholder="Ej. 5 a 7 días hábiles" value={form.tiempo_entrega} onChange={cambiar('tiempo_entrega')} /></Campo>

        <Campo etiqueta="Descripción" ancho="md:col-span-3"><textarea rows={2} className={ENTRADA} value={form.descripcion} onChange={cambiar('descripcion')} /></Campo>
        <Campo etiqueta="Requisitos de instalación" ancho="md:col-span-3"><textarea rows={2} className={ENTRADA} value={form.requisitos_instalacion} onChange={cambiar('requisitos_instalacion')} /></Campo>
        <Campo etiqueta="Argumentos de venta" ayuda="Lo que conviene resaltar al cliente" ancho="md:col-span-3"><textarea rows={2} className={ENTRADA} value={form.argumentos_venta} onChange={cambiar('argumentos_venta')} /></Campo>

        <label className="md:col-span-3 flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} />
          Activa (se ofrece a los clientes)
        </label>
      </form>

      {id && (
        <div className="mt-6 border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Fotos</h4>
            <label className={`inline-flex items-center gap-1 text-sm rounded border border-gray-300 px-3 py-1.5 cursor-pointer hover:bg-gray-50 ${subiendo ? 'opacity-50 pointer-events-none' : ''}`}>
              <ImagePlus size={16} /> {subiendo ? 'Procesando...' : 'Agregar fotos'}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={alElegirArchivos} />
            </label>
          </div>
          {fotos.length === 0 && <p className="text-sm text-gray-500">Todavía no hay fotos. JPG, PNG o WEBP de hasta 5 MB.</p>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {fotos.map((f) => (
              <div key={f.id} className={`relative rounded-lg overflow-hidden border ${f.es_principal ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'}`}>
                <img src={urlFoto(f.ruta)} alt={f.nombre_original || 'Foto de la máquina'} className="w-full h-32 object-cover" />
                <div className="absolute top-1 right-1 flex gap-1">
                  {!f.es_principal && (
                    <button type="button" title="Usar como principal" aria-label="Usar como principal" onClick={() => accionFoto(() => maquinasService.fotoPrincipal(id, f.id))} className="bg-white/90 rounded p-1 text-gray-700 hover:text-yellow-600">
                      <Star size={14} />
                    </button>
                  )}
                  <button type="button" title="Eliminar foto" aria-label="Eliminar foto" onClick={() => accionFoto(() => maquinasService.eliminarFoto(id, f.id))} className="bg-white/90 rounded p-1 text-gray-700 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
                {f.es_principal && <span className="absolute bottom-1 left-1 text-[10px] bg-green-600 text-white rounded px-1.5 py-0.5">Principal</span>}
              </div>
            ))}
          </div>

          <div className="mt-6 border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Documentos de esta máquina</h4>
            <DocumentosPanel maquinaId={id} />
          </div>
        </div>
      )}
    </Modal>
  );
};

const MaquinasView = () => {
  const [maquinas, setMaquinas] = useState([]);
  const [incluirInactivas, setIncluirInactivas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(null);
  const [verGenerales, setVerGenerales] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await maquinasService.listar({ incluirInactivas });
      setMaquinas(res.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las máquinas');
    } finally {
      setCargando(false);
    }
  }, [incluirInactivas]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (m) => {
    try {
      const res = await maquinasService.obtener(m.id);
      setEditando(res.data);
    } catch (err) {
      setError(err.message || 'No se pudo abrir la máquina');
    }
  };

  return (
    <div className="p-2 md:p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Store size={22} /> Catálogo de máquinas</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={incluirInactivas} onChange={(e) => setIncluirInactivas(e.target.checked)} />
            Incluir inactivas
          </label>
          <button type="button" onClick={() => setVerGenerales(true)} className="inline-flex items-center gap-1 rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <FileText size={16} /> Documentos generales
          </button>
          <button type="button" onClick={() => setEditando({})} className="inline-flex items-center gap-1 rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            <Plus size={16} /> Nueva máquina
          </button>
        </div>
      </div>

      {error && <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {!cargando && maquinas.length === 0 && !error && <p className="text-sm text-gray-500">Todavía no hay máquinas. Crea la primera con "Nueva máquina".</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {maquinas.map((m) => (
          <button key={m.id} type="button" onClick={() => abrir(m)} className={`text-left bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow ${m.activo ? '' : 'opacity-60'}`}>
            {m.foto_principal
              ? <img src={urlFoto(m.foto_principal)} alt={m.nombre} className="w-full h-44 object-cover" />
              : <div className="w-full h-44 bg-gray-100 flex items-center justify-center text-gray-400"><Store size={40} /></div>}
            <div className="p-4 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">{m.nombre}</h3>
                <span className="shrink-0 text-[11px] rounded-full bg-blue-50 text-blue-700 px-2 py-0.5">{etiquetaTipo(m.tipo)}</span>
              </div>
              <div className="text-lg font-semibold text-gray-900">{fmtPrecio(m.precio_venta, m.moneda)}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                <span className={m.stock > 0 ? 'text-green-700' : 'text-red-600'}>{m.stock > 0 ? `${m.stock} en stock` : 'Sin stock'}</span>
                {m.capacidad_unidades && <span>Capacidad {m.capacidad_unidades}</span>}
                {m.garantia_meses !== null && m.garantia_meses !== undefined && <span>Garantía {m.garantia_meses} m</span>}
                {!m.activo && <span className="text-gray-700 font-medium">Inactiva</span>}
              </div>
            </div>
          </button>
        ))}
      </div>

      {verGenerales && (
        <Modal titulo="Documentos generales" subtitulo="Catálogo completo, condiciones de venta: no dependen de una máquina" icono={FileText} ancho="xl" onClose={() => setVerGenerales(false)}>
          <DocumentosPanel maquinaId={null} />
        </Modal>
      )}

      {editando && (
        <FormularioMaquina
          key={editando.id || 'nueva'}
          inicial={editando.id ? editando : null}
          onGuardada={cargar}
          onClose={() => setEditando(null)}
        />
      )}
    </div>
  );
};

export default MaquinasView;
